import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
	page: number
	limit: number
	total: number
	onPageChange: (page: number) => void
}

// Numbered pages are only possible because these lists are backed by SQL and
// return a real total. The coupons list is Chargebee-backed and cursor-paged
// instead — see CursorPagination.
export function Pagination({ page, limit, total, onPageChange }: Props) {
	const pages = Math.max(1, Math.ceil(total / limit))
	if (total === 0) return null
	const from = (page - 1) * limit + 1
	const to = Math.min(page * limit, total)

	return (
		<div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
			<span className="text-ink-soft">
				{from}–{to} of {total}
			</span>
			<div className="flex items-center gap-2">
				<button
					type="button"
					className="btn-secondary px-2"
					disabled={page <= 1}
					onClick={() => onPageChange(page - 1)}
					aria-label="Previous page"
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
				<span className="text-ink-soft">
					{page} / {pages}
				</span>
				<button
					type="button"
					className="btn-secondary px-2"
					disabled={page >= pages}
					onClick={() => onPageChange(page + 1)}
					aria-label="Next page"
				>
					<ChevronRight className="h-4 w-4" />
				</button>
			</div>
		</div>
	)
}

// Chargebee pages with an opaque offset token, so there is no page count and no
// way back except remembering where we have been.
export function CursorPagination({
	cursors,
	nextCursor,
	onBack,
	onNext,
}: {
	cursors: string[]
	nextCursor: string | null
	onBack: () => void
	onNext: () => void
}) {
	if (cursors.length === 0 && !nextCursor) return null
	return (
		<div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3 text-sm">
			<button type="button" className="btn-secondary px-2" disabled={cursors.length === 0} onClick={onBack}>
				<ChevronLeft className="h-4 w-4" />
			</button>
			<button type="button" className="btn-secondary px-2" disabled={!nextCursor} onClick={onNext}>
				<ChevronRight className="h-4 w-4" />
			</button>
		</div>
	)
}
